import { useState } from 'react';
import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth';
import styles from 'styles/modules/newsAlert/TemplateFields.module.scss';
import inputStyles from 'styles/modules/Input.module.scss';
import Button from 'components/Button';
import {
    ALERT_QUESTIONS_HEADING,
    MAX_INTRO_WORDS,
    MAX_QUESTIONS,
    countWords,
    filledQuestions,
    introLimited,
    introText,
} from 'utils/alert-template';

const Editor = dynamic(() => import('components/Editor'), { ssr: false });

// The editor's full toolbar, as before the template; videos from YouTube and Vimeo
// only, the hosts the API keeps.
export const ALERT_EDITOR_CONFIG = {
    mediaEmbed: {
        previewsInData: true,
        removeProviders: [
            'dailymotion',
            'spotify',
            'instagram',
            'twitter',
            'googleMaps',
            'flickr',
            'facebook',
        ],
    },
};

let nextKey = 0;
const newKey = () => `question-${nextKey++}`;

const TemplateFields = ({ intro, questions, onChange, storedIntro }) => {
    const { isAdmin } = useSelector(authSelector);
    // Stable keys, so removing a middle question doesn't shift the others' inputs.
    const [keys, setKeys] = useState(() => questions.map(newKey));
    const words = countWords(introText(intro));
    // Over the limit blocks everyone but admins (who only see the note) and an author
    // saving an intro whose text they didn't change.
    const limited = introLimited(intro, isAdmin, storedIntro);
    const over = words > MAX_INTRO_WORDS;

    const setQuestion = (index, value) =>
        onChange({
            intro,
            questions: questions.map((q, i) => (i === index ? value : q)),
        });

    const addQuestion = () => {
        setKeys([...keys, newKey()]);
        onChange({ intro, questions: [...questions, ''] });
    };

    const removeQuestion = (index) => {
        setKeys(keys.filter((_, i) => i !== index));
        onChange({ intro, questions: questions.filter((_, i) => i !== index) });
    };

    return (
        <div className={styles.fields}>
            <p className={styles.label}>Intro</p>
            <Editor
                data={intro}
                setData={(html) => onChange({ intro: html, questions })}
                height="200px"
                config={ALERT_EDITOR_CONFIG}
                ariaLabel="Intro"
            />
            <p
                className={[
                    styles.count,
                    over && limited ? styles.over : '',
                ].join(' ')}
            >
                {words} / {MAX_INTRO_WORDS} words
                {over && isAdmin && ' (over the limit, allowed for admins)'}
            </p>

            <h2 className={styles.heading}>{ALERT_QUESTIONS_HEADING}</h2>
            <ol className={styles.questions}>
                {questions.map((question, index) => (
                    <li key={keys[index]} className={styles.question}>
                        <input
                            className={inputStyles.input}
                            value={question}
                            onChange={(e) => setQuestion(index, e.target.value)}
                            aria-label={`Question ${index + 1}`}
                            placeholder={`Question ${index + 1}`}
                        />
                        {questions.length > 1 && (
                            <button
                                type="button"
                                className={styles.remove}
                                onClick={() => removeQuestion(index)}
                            >
                                Remove
                            </button>
                        )}
                    </li>
                ))}
            </ol>
            {isAdmin && filledQuestions(questions).length === 0 && (
                <p className={styles.count}>
                    No questions (allowed for admins): the alert goes out
                    without this heading.
                </p>
            )}
            {questions.length < MAX_QUESTIONS && (
                <Button
                    title="Add question"
                    theme="outline"
                    onClick={addQuestion}
                    classes={[styles.add]}
                />
            )}
        </div>
    );
};

export default TemplateFields;
